/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_HIDE_HEADER_EMPTY_STATE
 * Date:         2026-01-01
 * Modified By:  Cursor
 *
 * Description:
 * Hide header (Create Broadcast title + Download/Upload buttons) when empty state is showing.
 * Header now only displays when form is visible for cleaner UX.
 *
 * File Size Check:
 * - Current: ~150 lines
 * - Changes: Conditional rendering only (no line count change)
 * - Projected: ~150 lines
 * - Decision: Proceed (within 200-line component limit)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/BroadcastCreatePage.tsx (modified)
 * - client/src/features/broadcast/broadcast-create/BroadcastCreateHeader.tsx
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_SIMPLIFIED_EMPTY_STATE
 * Date:         2026-01-01
 * Modified By:  Cursor
 *
 * Description:
 * Simplified empty state logic - always show empty state initially.
 * Removed useDraftBroadcasts API check for simpler UX.
 * Form shows only when user clicks "Create Broadcast" button.
 *
 * File Size Check:
 * - Current: ~150 lines
 * - Removing: ~30 lines (hook, loading state)
 * - Projected: ~120 lines
 * - Decision: Proceed (within 200-line component limit)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/BroadcastCreatePage.tsx (simplified)
 * - client/src/features/broadcast/broadcast-create/BroadcastEmptyState.tsx
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_EMPTY_STATE_INTEGRATION
 * Date:         2026-01-01
 * Modified By:  Cursor
 *
 * Description:
 * Integrated empty state component to display when user has no draft broadcasts.
 * Added useDraftBroadcasts hook to check for drafts.
 * Added showForm state to control form visibility after user clicks "Create Broadcast".
 * Also completed Phase 1 split by extracting header and upload logic into sub-components.
 *
 * File Size Check:
 * - Current: 248 lines
 * - Adding: ~25 lines (imports, state, conditional rendering)
 * - Removing: ~160 lines (extracted to components/hooks)
 * - Projected: ~115 lines
 * - Decision: Proceed (within 200-line component limit)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/BroadcastCreatePage.tsx (modified)
 * - client/src/features/broadcast/broadcast-create/BroadcastEmptyState.tsx
 * - client/src/features/broadcast/broadcast-create/useDraftBroadcasts.ts
 * - client/src/features/broadcast/broadcast-create/BroadcastCreateHeader.tsx
 * - client/src/features/broadcast/broadcast-create/useFileUpload.ts
 */

// [2026-02-07] style-extraction: moved inline styles to SCSS module
import React, { useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BroadcastForm from "./BroadcastForm";
import BroadcastEmptyState from "./broadcast-create/BroadcastEmptyState";
import BroadcastCreateHeader from "./broadcast-create/BroadcastCreateHeader";
import ProfileIncompleteState from "./broadcast-create/ProfileIncompleteState";
import { useFileUpload } from "./broadcast-create/useFileUpload";
import { useProfileStatus } from "../../hooks/useProfileStatus";
import { useDismissibleBanner } from "../../hooks/useDismissibleBanner";
import InfoBanner from "../../components/InfoBanner/InfoBanner";
import { listBroadcastsRaw } from "../../services/broadcasts";
import styles from "../../styles/admin-layout.module.scss";

const BroadcastCreatePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const profileStatus = useProfileStatus();

  const shouldShowForm = location.pathname === "/broadcast/edit";
  const isNewRoute = location.pathname === "/broadcast/new";

  const [formikInstance, setFormikInstance] = useState<any>(null);
  const [mineCheckLoading, setMineCheckLoading] = useState(isNewRoute);
  const [hasBroadcasts, setHasBroadcasts] = useState(false);

  const [isBannerDismissed, dismissBanner, bannerLoading] = useDismissibleBanner(
    "broadcast-multi-email-info"
  );

  useEffect(() => {
    if (!isNewRoute) return;
    let mounted = true;
    setMineCheckLoading(true);
    listBroadcastsRaw({ limit: 1 })
      .then((res) => {
        if (mounted) setHasBroadcasts((res.items?.length ?? 0) > 0);
      })
      .catch(() => {
        if (mounted) setHasBroadcasts(false);
      })
      .finally(() => {
        if (mounted) setMineCheckLoading(false);
      });
    return () => { mounted = false; };
  }, [isNewRoute]);

  useEffect(() => {
    if (!isNewRoute || mineCheckLoading) return;
    if (hasBroadcasts) navigate("/broadcast/mine-all", { replace: true });
  }, [isNewRoute, mineCheckLoading, hasBroadcasts, navigate]);

  const uploadHandlers = useFileUpload(formikInstance, () => navigate("/broadcast/edit"));

  // [2025-12-30] Fix: Memoize handleClearData to prevent recreation
  const handleClearData = useCallback(() => {
    // Reset file input value via ref from uploadHandlers
    if (uploadHandlers.fileInputRef.current) {
      uploadHandlers.fileInputRef.current.value = "";
    }

    // Reset form using formik instance
    if (formikInstance?.resetForm) {
      formikInstance.resetForm();
    }
  }, [formikInstance, uploadHandlers.fileInputRef]); // [2025-12-30] Fix: Depend on formikInstance

  // [2025-12-30] Fix: Create proper ref callback that only updates when value changes
  const formikRefCallback = useCallback((instance: any) => {
    // Only update state if the instance actually changed
    setFormikInstance((prev: any) => {
      if (prev === instance) {
        return prev; // No change, don't trigger re-render
      }
      return instance;
    });
  }, []); // [2025-12-30] Fix: Empty deps - setFormikInstance is stable

  if (profileStatus.error) {
    return (
      <div className={`container ${styles["full-width-container"]}`}>
        <div className="row g-0">
          <main className="col-12 bg-white">
            <div className="p-3">{profileStatus.error}</div>
          </main>
        </div>
      </div>
    );
  }

  // BR-26: Profile incomplete — show "Complete your profile" block
  if (!profileStatus.loading && !profileStatus.canBroadcast) {
    return (
      <div className={`container ${styles["full-width-container"]}`}>
        <div className="row g-0">
          <main className="col-12 bg-white">
            <div className="p-3">
              <ProfileIncompleteState
                missingFields={profileStatus.missingFields}
                onGoToCompanyProfile={() => navigate("/setup?section=company")}
                onGoToUserProfile={() => navigate("/setup?section=profile")}
              />
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (profileStatus.loading) {
    return (
      <div className={`container ${styles["full-width-container"]}`}>
        <div className="row g-0">
          <main className="col-12 bg-white">
            <div className="p-3">Checking profile…</div>
          </main>
        </div>
      </div>
    );
  }

  if (isNewRoute && (mineCheckLoading || hasBroadcasts)) {
    if (hasBroadcasts) return null;
    return (
      <div className={`container ${styles["full-width-container"]}`}>
        <div className="row g-0">
          <main className="col-12 bg-white">
            <div className="p-3">Loading…</div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={`container ${styles["full-width-container"]}`}>
      {shouldShowForm && <BroadcastCreateHeader {...uploadHandlers} />}
      <div className="row g-0">
        <main className="col-12 bg-white">
          <div className="p-3">
            {shouldShowForm ? (
              <>
                {!bannerLoading && (
                  <InfoBanner
                    bannerId="broadcast-multi-email-info"
                    title="For your information"
                    message="When creating multiple broadcasts, each one will be sent as a separate email."
                    isDismissed={isBannerDismissed}
                    onDismiss={() => dismissBanner()}
                  />
                )}
                <BroadcastForm ref={formikRefCallback} onClearData={handleClearData} />
              </>
            ) : (
              <BroadcastEmptyState onCreateClick={() => navigate("/broadcast/edit")} />
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default BroadcastCreatePage;
